import {
  Box,
  Button,
  Flex,
  Group,
  LoadingOverlay,
  NumberInput,
  Stack,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import TextEditor from "../../../../components/ExternalEditors/TextEditor";
import { useEffect, useMemo, useRef, useState } from "react";
import { useConfigStore } from "../../../../stores/ConfigStore";
import { getFields } from "../../../../api/ContactAPI";
import { useLanguage } from "../../../../stores/LanguageStore";
import {
  editLinkedinInvitation,
  editLinkedinMessage,
  editSMS,
  findLinkedinInvitation,
  findLinkedinMessage,
  findSMS,
  generateLinkedinInvitation,
  generateLinkedinMessage,
  getLinkedinInvitations,
  getLinkedinMessages,
  getSMS,
} from "../../../../api/ModelsApi";
import { notifications } from "@mantine/notifications";
import MessageModalPreview from "../../../../components/View/MessageModalPreview";
import useNavigationBlocker from "../../../../components/View/NavigationBlocker";
import BackButton from "../../../../components/View/BackButton";
import Generator from "../emails/Generator";
import { modals } from "@mantine/modals";
import FilesModal from "../../files/FilesModal";
import { findFile } from "../../../../api/FIleAPI";
import FileItemMessage from "./FileItemMessage";
import SmsSenderField from "../../../../components/View/SmsSenderField";
import { useProfileStore } from "../../../../stores/UserStore";
import AddTokens from "../../../../components/Adder/AddTokens";
import GlobalLanguageSelector from "../../../../components/Selectors/GlobalLanguageSelector";
import TemperatureSlider from "../../../../components/View/TemperatureSlider";
import { IconBaselineDensityMedium } from "@tabler/icons-react";

const EditModelLinkedin = ({
  handleClose,
  type,
  onEdited,
  model,
  maxChars = 280,
}) => {
  const editorRef = useRef();
  const lang = useLanguage((s) => s.language);
  const datafields = useConfigStore((s) => s.datafields);
  const [loading, setLoading] = useState(model?.text ? false : true);
  const [dirty, setDirty] = useState(false);
  const datafieldTokens = useMemo(() => {
    let df = datafields.map((x) => ({
      label: x.name,
      value: x.identifier_placeholder,
    }));
    return df;
  }, [datafields]);

  const [modelProfile, setModelProfile] = useState(model);
  const [file, setFile] = useState(null);

  useEffect(() => {
    if (!model?.text) {
      setLoading(true);

      let func;
      if (type === "linkedin_message") {
        func = findLinkedinMessage;
      }
      if (type === "linkedin_invitation") {
        func = findLinkedinInvitation;
      }
      if (type === "sms") {
        func = findSMS;
      }

      func(model?.id)
        .then((response) => {
          setModelProfile(response.data.model_profile);

          editorRef.current?.setContent(
            response.data.model_profile?.text?.replaceAll("\n", "<br />"),
          );
          if (response.data.model_profile?.file_id) {
            findFile(response.data.model_profile.file_id).then((res) => {
              setFile(res.data.file);
            });
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [model, type]);

  useEffect(() => {
    if (modelProfile?.file_id) {
      findFile(modelProfile.file_id).then((res) => {
        setFile(res.data.file_profile);
      });
    }
  }, [modelProfile]);

  useEffect(() => {
    getFields();
  }, []);

  const handlePreview = ({ setText, setSender, open }) => {
    const fd = new FormData(document.getElementById("model-form"));
    const data = Object.fromEntries(fd);

    setText(editorRef.current.getContent({ format: "text" }));
    setSender(data?.sender);
    open();
  };

  const [submitting, setSubmitting] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const fd = new FormData(e.target);
    const data = Object.fromEntries(fd);

    let text = editorRef?.current?.getContent({ format: "text" });

    let func;
    if (type === "linkedin_message") {
      func = editLinkedinMessage;
    }
    if (type === "linkedin_invitation") {
      func = editLinkedinInvitation;
    }
    if (type === "sms") {
      func = editSMS;
    }
    return func({
      id: model?.id,
      name: data.name,
      text,
      sender: data?.sender,
      file_id: file?.id,
      ai_number_paragraphs: data?.ai_number_paragraphs
        ? parseInt(data?.ai_number_paragraphs)
        : null,
      ai_language: data?.ai_language,
      ai_prompt: data?.ai_prompt,
      ai_temperature: parseFloat(data?.temperature),
    })
      .then((response) => {
        let newId = response.data.model_id;
        setSubmitting(true);

        let funcGetter;
        if (type === "linkedin_message") {
          funcGetter = getLinkedinMessages;
        }
        if (type === "linkedin_invitation") {
          funcGetter = getLinkedinInvitations;
        }
        if (type === "sms") {
          funcGetter = getSMS;
        }

        funcGetter()
          .then((res) => {
            let newItemsList = res.data.models_list;
            let newItem = newItemsList.find((x) => x.id === newId);
            notifications.show({
              title: lang.models.edit_success,
              message: lang.models.edit_success_text,
              color: "tertiary.8",
            });
            setDirty(false);

            setTimeout(() => {
              if (onEdited) {
                onEdited(newItem);
              }
            }, 50);
          })
          .finally(() => {
            setSubmitting(false);
          });
      })
      .catch(() => setSubmitting(false));
  };

  const onGenerated = (html) => {
    if (dirty) {
      return modals.openConfirmModal({
        title: lang.models.generator.confirm.title,
        children: <Text size="sm">{lang.models.generator.confirm.text}</Text>,
        labels: {
          confirm: lang.models.generator.confirm.replace,
          cancel: lang.global.cancel,
        },
        onCancel: () => {},
        onConfirm: () => {
          editorRef.current?.setContent(html);
        },
        centered: true,
      });
    }

    editorRef.current?.setContent(html);
  };

  const handleFileAdd = (content) => {
    setFile(content.item);
  };

  useNavigationBlocker(dirty);

  return (
    <>
      <LoadingOverlay
        visible={loading || submitting}
        overlayProps={{ blur: 1 }}
        zIndex={1001}
      />

      <Stack
        component={"form"}
        onSubmit={onSubmit}
        gap={0}
        id="model-form"
        flex={1}
      >
        <Group wrap="nowrap" mb={"sm"}>
          <TextInput
            key={modelProfile?.id}
            label={lang.model_emails.create.dialog.name}
            placeholder={lang.model_emails.create.dialog.name}
            defaultValue={modelProfile?.name}
            name="name"
            w="50%"
            required
            data-autofocus
            autoFocus
            onChange={() => setDirty(true)}
            size="md"
          />
          {type === "sms" && (
            <SmsSenderField
              name="sender"
              label={lang.components.email_preview.sender}
              placeholder={lang.components.email_preview.sender}
              defaultValue={modelProfile?.sender}
              size="md"
              w="50%"
              required={false}
            />
          )}
        </Group>

        {loading ? (
          <Stack h={300}></Stack>
        ) : modelProfile?.is_ai ? (
          <>
            <Group pos={"relative"} wrap="nowrap" gap={0} align="stretch">
              <Textarea
                label={"Prompt"}
                id="ai_prompt"
                placeholder={lang.quick_campaign.prompt_placeholder}
                description={lang.quick_campaign.prompt_desc}
                name="ai_prompt"
                defaultValue={modelProfile?.prompt}
                rows={8}
                w={"100%"}
                maxLength={
                  useProfileStore.getState().profile?.id === 2544 ? 10000 : 4000
                }
                mt={12}
                required
                size="md"
              />
              <AddTokens
                items={datafieldTokens}
                handleAdd={(item) => {
                  let input = document.getElementById("ai_prompt");
                  input.value += `{${item?.value.replaceAll("%", "")}}`;
                  input.focus();
                }}
                long
                buttonProps={{
                  size: "xs",
                  variant: "subtle",
                  style: {
                    position: "absolute",
                    top: 16,
                    right: 0,
                  },
                }}
              />
            </Group>

            <Group wrap="nowrap" gap={24} mt={12} mb={16}>
              <GlobalLanguageSelector
                name={"ai_language"}
                withinPortal={false}
                size="sm"
                w={"100%"}
                description={lang.quick_campaign.language_desc}
                defaultValue={modelProfile?.ai_language}
              />

              <TemperatureSlider
                desc={lang.quick_campaign.temperature_desc}
                defaultValue={modelProfile?.ai_temperature}
              />

              <NumberInput
                name="ai_number_paragraphs"
                label={lang.model_emails.create.dialog.paragraphs}
                description={lang.quick_campaign.paragraph_desc}
                defaultValue={modelProfile?.ai_number_paragraphs}
                leftSection={
                  <Tooltip label={lang.model_emails.create.dialog.paragraphs}>
                    <IconBaselineDensityMedium size={16} />
                  </Tooltip>
                }
                w={"100%"}
                size="sm"
                required
                mr={"auto"}
                min={1}
                max={4}
              />
            </Group>
          </>
        ) : (
          <TextEditor
            editorRef={editorRef}
            placeholder={lang.model_emails.create.dialog.placeholder}
            tokens={datafieldTokens}
            // noTop
            toolbar={"undo redo | tokens |"}
            onKeyUp={() => setDirty(true)}
            maxChars={maxChars}
            defaultText={modelProfile?.text}
            onInit={(editor) =>
              editor.setContent(modelProfile?.text?.replaceAll("\n", "<br />"))
            }
            longVariableButton
          >
            <Group
              wrap="nowrap"
              style={{
                position: "absolute",
                top: 9,
                left: 136,
                zIndex: 4,
              }}
            >
              <Generator
                handler={
                  type === "linkedin_message"
                    ? generateLinkedinMessage
                    : generateLinkedinInvitation
                }
                isLinkedin
                onFinish={onGenerated}
                isLinkedinInvitation={type === "linkedin_invitation"}
              />
            </Group>
          </TextEditor>
        )}

        {type === "linkedin_invitation" && (
          <Text mt={"xs"} c={"red"} size="sm">
            {lang.model_invitation_linkedin.create.dialog.info_limit}
          </Text>
        )}

        {file && <FileItemMessage file={file} setFile={setFile} />}

        <Group justify="space-between" mt={"sm"}>
          <Flex>
            {type === "linkedin_message" && (
              <>
                <Box>
                  <FilesModal
                    isNew
                    isLinkedin
                    handleFileAdd={handleFileAdd}
                    isLongButton
                    buttonProps={{ size: "md", h: "100%", mr: "md" }}
                  />
                </Box>

                {/* <Box>
                  <FilesModal
                    isNew
                    isLinkedin
                    handleFileAdd={handleFileAdd}
                    isLongButton
                    buttonProps={{ size: "md", h: "100%", mr: "md" }}
                    defaultType={"audio"}
                  />
                </Box> */}
              </>
            )}
            <MessageModalPreview
              getData={handlePreview}
              type={type}
              size="md"
            />
          </Flex>
          <Flex gap={"md"}>
            <BackButton
              onClick={handleClose}
              label={lang.global.back}
              size="md"
            />
            <Button type="submit" size="md">
              {lang.model_emails.edit.dialog.submit_button}
            </Button>
          </Flex>
        </Group>
      </Stack>
    </>
  );
};

export default EditModelLinkedin;
